import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInsightLog } from './useInsightLog';
import { patients } from '../data/patients';
import { MANUAL_IU_MAX, MANUAL_IU_MIN, OTHER_DOSE, productById } from '../data/products';
import { responseZones } from '../data/responseZones';
import { rekovelleDose } from '../utils/rekovelleDose';
import { zoneFor } from '../utils/responseModel';
import { formatDose, formatTreatment, modelPosition, sameTreatment } from '../utils/treatmentModel';
import type { PatientPreset, Step } from '../types/simulation';
import type { ProductId, Treatment } from '../types/treatment';

export type Screen = 'intro' | 'case';

export function useSimulator() {
  const { events, log, clear } = useInsightLog();

  const [screen, setScreen] = useState<Screen>('intro');
  const [patientIndex, setPatientIndex] = useState(0);
  const preset = patients[patientIndex];
  const [weight, setWeight] = useState(preset.weight);
  const [amh, setAmh] = useState(preset.amh);
  const patient: PatientPreset = useMemo(() => ({ ...preset, weight, amh }), [preset, weight, amh]);
  const indiv = useMemo(() => rekovelleDose(amh, weight), [amh, weight]);
  const adjusted = weight !== preset.weight || amh !== preset.amh;

  const [step, setStep] = useState<Step>(1);
  const [productId, setProductId] = useState<ProductId | null>(null);
  const [doseSelection, setDoseSelection] = useState<string | null>(null);
  const [manualDose, setManualDose] = useState('');
  const [manualError, setManualError] = useState<string | null>(null);
  const [treatment, setTreatment] = useState<Treatment | null>(null);
  const [hasRun, setHasRun] = useState(false);
  const [runKey, setRunKey] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const revealedTreatment = useRef<Treatment | null>(null);

  useEffect(() => {
    if (screen !== 'case') return;
    log('case_shown', preset.id, { age: preset.age, weight: preset.weight, amh: preset.amh, profile: preset.profile });
  }, [screen, preset, log]);

  const clearRun = useCallback(() => {
    setTreatment(null);
    setHasRun(false);
    setRevealed(false);
    revealedTreatment.current = null;
  }, []);

  const loadPatient = (index: number) => {
    setPatientIndex(index);
    setWeight(patients[index].weight);
    setAmh(patients[index].amh);
    setProductId(null);
    setDoseSelection(null);
    setManualDose('');
    setManualError(null);
    clearRun();
    setStep(1);
  };

  // --- Intro & navigation ---
  const startFromIntro = () => {
    log('intro_completed', preset.id);
    setScreen('case');
  };
  const showIntro = () => setScreen('intro');

  const goBack = () => {
    if (step === 1) setScreen('intro');
    else setStep((step - 1) as Step);
  };

  const canNavigate = (s: number) => {
    if (s === 5) return false;
    if (s === 4) return treatment !== null && step !== 4;
    if (s === 3) return productId !== null && step > 3;
    return s < step;
  };
  const navigateTo = (s: number) => {
    if (canNavigate(s)) setStep(s as Step);
  };

  const goToPatient = (index: number, via: 'nav' | 'skip' | 'cta') => {
    if (index === patientIndex && via === 'nav') return;
    log('next_patient', preset.id, { to: patients[index].id, via });
    loadPatient(index);
  };
  const nextIndex = (patientIndex + 1) % patients.length;
  const skipPatient = () => goToPatient(nextIndex, 'skip');
  const nextPatient = () => goToPatient(nextIndex, 'cta');
  const selectPatient = (index: number) => goToPatient(index, 'nav');

  const replayCase = () => {
    log('case_replayed', preset.id, { fromStep: step });
    loadPatient(patientIndex);
  };

  // --- Step 1: patient ---
  const adjustPatient = (patch: { weight?: number; amh?: number }) => {
    if (patch.weight !== undefined) setWeight(patch.weight);
    if (patch.amh !== undefined) setAmh(patch.amh);
    clearRun();
  };
  const resetPatientValues = () => adjustPatient({ weight: preset.weight, amh: preset.amh });

  const startCase = () => {
    log('case_started', preset.id, { weight, amh, adjusted });
    setStep(2);
  };

  // --- Step 2: product ---
  const selectProduct = (id: ProductId) => {
    if (id !== productId) {
      setTreatment(null);
      setHasRun(false);
    }
    setProductId(id);
    setDoseSelection(null);
    setManualDose('');
    setManualError(null);
  };
  const confirmProduct = () => {
    if (!productId) return;
    log('drug_selected', preset.id, { product: productById(productId).name });
    setStep(3);
  };

  // --- Step 3: dose ---
  const selectDose = (v: string) => {
    setDoseSelection(v);
    setManualError(null);
  };
  const changeManualDose = (v: string) => {
    setManualDose(v);
    setManualError(null);
  };
  const doseReady =
    productId === 'rekovelle' || (doseSelection !== null && (doseSelection !== OTHER_DOSE || manualDose.trim() !== ''));

  const confirmDose = () => {
    if (!productId) return;
    const product = productById(productId);
    let next: Treatment;
    if (productId === 'rekovelle') {
      next = { product: productId, dose: indiv.dose, unit: 'μg', manual: false };
    } else if (doseSelection === OTHER_DOSE) {
      const v = Number(manualDose);
      if (!manualDose || Number.isNaN(v) || v < MANUAL_IU_MIN || v > MANUAL_IU_MAX) {
        setManualError(`Enter a dose between ${MANUAL_IU_MIN} and ${MANUAL_IU_MAX} IU.`);
        return;
      }
      next = { product: productId, dose: v, unit: product.unit, manual: true };
    } else if (doseSelection) {
      next = { product: productId, dose: Number(doseSelection), unit: product.unit, manual: false };
    } else {
      return;
    }
    setTreatment(next);
    setHasRun(false);
    log('dose_selected', preset.id, { product: product.name, dose: formatDose(next.dose, next.unit), manual: next.manual });
    setStep(4);
  };

  // --- Step 4/5: response & reveal ---
  const runResponse = () => {
    if (!treatment) return;
    const position = modelPosition(patient, treatment);
    setHasRun(true);
    setRunKey((k) => k + 1);
    log('response_run', preset.id, {
      choice: formatTreatment(treatment),
      tendency: responseZones[zoneFor(position)].short,
      modelPosition: Math.round(position * 10) / 10,
    });
    const prior = revealedTreatment.current;
    if (prior && !sameTreatment(prior, treatment)) {
      log('choice_changed_after_reveal', preset.id, { from: formatTreatment(prior), to: formatTreatment(treatment) });
      revealedTreatment.current = treatment;
    }
  };
  const replayResponse = () => setRunKey((k) => k + 1);

  const reveal = () => {
    if (!treatment) return;
    setRevealed(true);
    revealedTreatment.current = treatment;
    log('reveal_clicked', preset.id, { current: formatTreatment(treatment), rekovelle: `${indiv.dose.toFixed(2)} μg` });
  };

  const changeChoice = () => {
    setHasRun(false);
    setStep(2);
  };

  const zone = hasRun && treatment ? responseZones[zoneFor(modelPosition(patient, treatment))] : null;
  const displayStep = step === 4 && hasRun && revealed ? 5 : step;

  return {
    events,
    clearEvents: clear,
    screen,
    patients,
    patientIndex,
    patient,
    adjusted,
    indiv,
    step,
    displayStep,
    productId,
    doseSelection,
    manualDose,
    manualError,
    doseReady,
    treatment,
    hasRun,
    runKey,
    revealed,
    zone,
    startFromIntro,
    showIntro,
    goBack,
    canNavigate,
    navigateTo,
    skipPatient,
    nextPatient,
    selectPatient,
    replayCase,
    adjustPatient,
    resetPatientValues,
    startCase,
    selectProduct,
    confirmProduct,
    setStep,
    selectDose,
    changeManualDose,
    confirmDose,
    runResponse,
    replayResponse,
    reveal,
    changeChoice,
  };
}
